import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export interface StepDescriptor {
  /** Stable key (used as the underlying form field group, route, etc). */
  id: string;
  /** Short label shown beneath the indicator. */
  label: string;
  /** Optional sublabel ("Required", "1 of 3"). */
  description?: string;
  /** Optional Lucide icon — falls back to step number when omitted. */
  icon?: React.ComponentType<{ className?: string }>;
}

interface StepperProps {
  steps: StepDescriptor[];
  /** Zero-based index of the current step. */
  current: number;
  /** Steps the user has already validated and could click back into. */
  completed?: Set<number> | number[];
  onStepClick?: (index: number) => void;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}

export function Stepper({
  steps,
  current,
  completed = [],
  onStepClick,
  className,
  orientation = 'horizontal',
}: StepperProps) {
  const doneSet = completed instanceof Set ? completed : new Set(completed);

  return (
    <ol
      className={cn(
        orientation === 'horizontal'
          ? 'flex w-full items-start gap-2'
          : 'flex w-full flex-col gap-3',
        className,
      )}
      aria-label="Progress"
    >
      {steps.map((step, idx) => {
        const isCurrent = idx === current;
        const isDone = doneSet.has(idx) || idx < current;
        const isClickable = Boolean(onStepClick) && (isDone || isCurrent);
        const Icon = step.icon;

        return (
          <li
            key={step.id}
            className={cn(
              'flex flex-1 items-center',
              orientation === 'vertical' && 'w-full',
            )}
          >
            <button
              type="button"
              onClick={() => isClickable && onStepClick?.(idx)}
              disabled={!isClickable}
              className={cn(
                'group flex items-center gap-3 text-left',
                orientation === 'horizontal' && 'flex-1',
                !isClickable && 'cursor-default',
              )}
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors',
                  isDone &&
                    'border-primary bg-primary text-primary-foreground',
                  !isDone && isCurrent && 'border-primary text-primary',
                  !isDone && !isCurrent && 'border-border bg-card text-muted-foreground',
                )}
              >
                {isDone ? (
                  <Check className="h-4 w-4" />
                ) : Icon ? (
                  <Icon className="h-4 w-4" />
                ) : (
                  idx + 1
                )}
              </span>
              <span className="flex flex-col">
                <span
                  className={cn(
                    'text-sm font-medium',
                    isCurrent ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {step.label}
                </span>
                {step.description && (
                  <span className="text-xs text-muted-foreground">{step.description}</span>
                )}
              </span>
            </button>

            {idx < steps.length - 1 && (
              <span
                aria-hidden
                className={cn(
                  orientation === 'horizontal'
                    ? 'mx-2 h-px flex-1 bg-border'
                    : 'my-1 ml-4 h-6 w-px bg-border',
                  isDone && 'bg-primary',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Hook to manage wizard state — current step + completed set + navigation
 * helpers. The form schemas remain per-step (one zod schema per step) so
 * advancing validates only the current step.
 */
export function useStepper(stepCount: number, initial = 0) {
  const [current, setCurrent] = React.useState(initial);
  const [completed, setCompleted] = React.useState<Set<number>>(new Set());

  const goTo = React.useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= stepCount) return;
      setCurrent(idx);
    },
    [stepCount],
  );

  const next = React.useCallback(() => {
    setCompleted((prev) => new Set(prev).add(current));
    if (current < stepCount - 1) setCurrent(current + 1);
  }, [current, stepCount]);

  const prev = React.useCallback(() => {
    if (current > 0) setCurrent(current - 1);
  }, [current]);

  const reset = React.useCallback(() => {
    setCurrent(0);
    setCompleted(new Set());
  }, []);

  return {
    current,
    completed,
    isFirst: current === 0,
    isLast: current === stepCount - 1,
    goTo,
    next,
    prev,
    reset,
  };
}
